/**
 * @fileoverview Create / edit form for the organisation details.
 * @description The legal, billing and bank details that Send Payment Details emails to a bank.
 * Validates the same rules as the backend (a legal name and the six bank fields required; CIN, GSTIN,
 * PAN, pincode, phone and emails checked when filled), upper-cases the codes, and saves through
 * Update Organisation Details with the version it was opened on as `If-Match` (none for the very first
 * save). Super Admin only, and the backend needs a recent MFA sign-in, so a refusal shows as the
 * form's error with a Sign in again link. A save made against a version that has since moved on is
 * refused and shown for review.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { useRefreshSalesQueries } from '@/features/sales/hooks/useRefreshSalesQueries'
import { useSaveOrganisationDetails } from '@/features/sales/hooks/useSaveOrganisationDetails'
import { isStaleMyPageError } from '@/features/sales/lib/my-page-stale.utils'
import { organisationDetailsFormSchema } from '@/features/sales/schemas/organisation-details-form.schema'
import FormField from '@/shared/components/molecules/FormField'
import SelectField from '@/shared/components/molecules/SelectField'
import { useGuardedClose } from '@/shared/hooks/useGuardedClose'
import { toTestIdSegment } from '@/shared/lib/test-id/test-id.utils'

import DialogActions from './DialogActions'
import SalesRequestError from './SalesRequestError'
import StaleVersionNotice from './StaleVersionNotice'

import type { OrganisationDetailsFormValues } from '@/features/sales/schemas/organisation-details-form.schema'
import type {
  OrganisationDetails,
  OrganisationDetailsInput,
} from '@/features/sales/types/my-page.types'
import type { FormEvent } from 'react'

interface OrganisationDetailsFormProps {
  details: OrganisationDetails | null
  onSaved: () => void
  onCancel: () => void
}

/** @description The optional text fields, in the order the form shows them. */
const GENERAL_FIELDS = [
  'tradeName',
  'cin',
  'gstin',
  'pan',
  'registeredAddress',
  'city',
  'state',
  'pincode',
  'billingContactName',
  'billingEmail',
  'billingPhone',
  'supportEmail',
] as const

/** @description The bank fields typed as text, in the order the form shows them. */
const BANK_TEXT_FIELDS = ['beneficiaryName', 'bankName', 'branch', 'accountNumber', 'ifsc'] as const

/** @description The form's values for details never saved: every field blank. */
const EMPTY_VALUES: OrganisationDetailsFormValues = {
  legalName: '',
  tradeName: '',
  cin: '',
  gstin: '',
  pan: '',
  registeredAddress: '',
  city: '',
  state: '',
  pincode: '',
  billingContactName: '',
  billingEmail: '',
  billingPhone: '',
  supportEmail: '',
  bank: {
    beneficiaryName: '',
    bankName: '',
    branch: '',
    accountNumber: '',
    ifsc: '',
    accountType: 'CURRENT',
  },
}

/**
 * @description The form's values for saved details (a cleared field is blank).
 * @param {OrganisationDetails | null} details - The saved details, or null when none exist.
 * @returns {OrganisationDetailsFormValues} The values to start the form with.
 */
function valuesOf(details: OrganisationDetails | null): OrganisationDetailsFormValues {
  if (!details) return EMPTY_VALUES
  return {
    legalName: details.legalName,
    tradeName: details.tradeName ?? '',
    cin: details.cin ?? '',
    gstin: details.gstin ?? '',
    pan: details.pan ?? '',
    registeredAddress: details.registeredAddress ?? '',
    city: details.city ?? '',
    state: details.state ?? '',
    pincode: details.pincode ?? '',
    billingContactName: details.billingContactName ?? '',
    billingEmail: details.billingEmail ?? '',
    billingPhone: details.billingPhone ?? '',
    supportEmail: details.supportEmail ?? '',
    bank: { ...details.bank },
  }
}

/**
 * @description What the form saves: blank optional fields become null (which clears them) and the
 * codes are upper-cased.
 * @param {OrganisationDetailsFormValues} values - The validated form values.
 * @returns {OrganisationDetailsInput} The body to send.
 */
function toInput(values: OrganisationDetailsFormValues): OrganisationDetailsInput {
  /**
   * @description A blank optional field as null, otherwise the text (upper-cased for a code).
   * @param {string} value - The field's value.
   * @param {boolean} [upper] - Whether the field is a code to upper-case.
   * @returns {string | null} What to send.
   */
  const optional = (value: string, upper = false): string | null =>
    value === '' ? null : upper ? value.toUpperCase() : value

  return {
    legalName: values.legalName,
    tradeName: optional(values.tradeName),
    cin: optional(values.cin, true),
    gstin: optional(values.gstin, true),
    pan: optional(values.pan, true),
    registeredAddress: optional(values.registeredAddress),
    city: optional(values.city),
    state: optional(values.state),
    pincode: optional(values.pincode),
    billingContactName: optional(values.billingContactName),
    billingEmail: optional(values.billingEmail),
    billingPhone: optional(values.billingPhone),
    supportEmail: optional(values.supportEmail),
    bank: { ...values.bank, ifsc: values.bank.ifsc.toUpperCase() },
  }
}

/**
 * @component
 * @param {OrganisationDetailsFormProps} props - The details being edited and the handlers.
 * @param {OrganisationDetails | null} props.details - The saved details, or null for the first save.
 * @param {() => void} props.onSaved - Called after a successful save.
 * @param {() => void} props.onCancel - Called to leave the form without saving.
 */
function OrganisationDetailsForm({ details, onSaved, onCancel }: OrganisationDetailsFormProps) {
  const { t } = useTranslation()
  const save = useSaveOrganisationDetails(onSaved)
  const refreshSales = useRefreshSalesQueries()
  const cancel = useGuardedClose(onCancel, save.isPending)
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<OrganisationDetailsFormValues>({
    resolver: zodResolver(organisationDetailsFormSchema),
    defaultValues: valuesOf(details),
  })
  const isStale = save.isError && isStaleMyPageError(save.error)

  /**
   * @description Translates a validation message key, if there is one.
   * @param {string | undefined} key - The message key from the schema.
   * @returns {string | undefined} The translated message.
   */
  const message = (key: string | undefined): string | undefined => (key ? t(key) : undefined)

  /**
   * @description Saves the validated details against the version the form was opened on.
   * @param {OrganisationDetailsFormValues} values - The form's values.
   */
  const onValid = (values: OrganisationDetailsFormValues) => {
    save.mutate({ input: toInput(values), version: details?.version ?? null })
  }

  /**
   * @description Reloads the latest details and leaves the form so they can be reviewed.
   */
  const onRefresh = () => {
    refreshSales()
    onCancel()
  }

  /**
   * @description Validates and submits the form.
   * @param {FormEvent<HTMLFormElement>} event - The submit event.
   */
  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    void handleSubmit(onValid)(event)
  }

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="flex flex-col gap-4"
      data-testid="organisation-details-form"
    >
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        <FormField
          id="orgLegalName"
          size="md"
          data-testid="organisation-legal-name-input"
          label={t('sales.myWork.organisation.fields.legalName')}
          error={message(errors.legalName?.message)}
          {...register('legalName')}
        />
        {GENERAL_FIELDS.map((name) => (
          <FormField
            key={name}
            id={`org-${name}`}
            size="md"
            data-testid={`organisation-${toTestIdSegment(name)}-input`}
            label={t(`sales.myWork.organisation.fields.${name}`)}
            error={message(errors[name]?.message)}
            {...register(name)}
          />
        ))}
      </div>

      <span className="text-[11px] font-bold tracking-[0.8px] text-kicker">
        {t('sales.myWork.organisation.bankTitle')}
      </span>
      <div className="grid grid-cols-1 gap-3.5 sm:grid-cols-2">
        {BANK_TEXT_FIELDS.map((name) => (
          <FormField
            key={name}
            id={`org-bank-${name}`}
            size="md"
            data-testid={`organisation-bank-${toTestIdSegment(name)}-input`}
            label={t(`sales.myWork.organisation.bank.${name}`)}
            error={message(errors.bank?.[name]?.message)}
            {...register(`bank.${name}`)}
          />
        ))}
        <SelectField
          id="org-bank-accountType"
          data-testid="organisation-bank-account-type-select"
          label={t('sales.myWork.organisation.bank.accountType')}
          options={[
            { value: 'CURRENT', label: t('sales.myWork.organisation.accountTypes.CURRENT') },
            { value: 'SAVINGS', label: t('sales.myWork.organisation.accountTypes.SAVINGS') },
          ]}
          {...register('bank.accountType')}
        />
      </div>

      {isStale && <StaleVersionNotice onRefresh={onRefresh} isRefreshing={false} />}
      {save.isError && !isStale && <SalesRequestError error={save.error} />}
      <DialogActions
        testIdPrefix="organisation-details"
        cancelLabel={t('sales.myWork.dialogs.cancel')}
        confirmLabel={t('sales.myWork.organisation.save')}
        onCancel={cancel}
        confirmType="submit"
        isLoading={save.isPending}
        disabled={isStale}
      />
    </form>
  )
}

export default OrganisationDetailsForm
